import { useQuery, useMutation, useQueryClient } from 'react-query'
import axios from 'axios'
import toast from 'react-hot-toast'

// API base URL configuration
// Both dev and prod use a relative path so requests are same-origin.
// - Dev: Vite proxies '/api' → http://localhost:8001 (see vite.config.js)
// - Prod: Resource Consents proxies '/leadsource/api' → backend
// (Hitting localhost:8001 directly from a non-8003 dev port triggered CORS.)
export const API_BASE_URL = import.meta.env.DEV
  ? '/api'
  : '/leadsource/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
})

// Session-expiry handling: sessions are 24h (7 days with remember-me), so a
// session can expire while a page is open. Without this, a 401 just renders
// "Error loading listings"-style messages and react-query retries in vain.
// Notify AuthContext (which flips to logged-out so ProtectedRoute redirects to
// /login) on any 401 from an authenticated endpoint. /auth/* is excluded:
// those endpoints have their own 401 semantics (login failures, admin-only
// checks). Public pages use the separate publicApi instance and never
// trigger this.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || ''
    if (error.response?.status === 401 && !url.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent('leadsource:unauthorized'))
    }
    return Promise.reject(error)
  }
)

// Listings API
export const useListings = (params = {}, options = {}) => {
  return useQuery(
    ['listings', params],
    async () => {
      const { data } = await api.get('/listings', { params })
      return data
    },
    { keepPreviousData: true, ...options }
  )
}

export const useListing = (id) => {
  return useQuery(
    ['listing', id],
    async () => {
      const { data } = await api.get(`/listings/${id}`)
      return data
    },
    { enabled: !!id }
  )
}

export const useCreateListing = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    async (listingData) => {
      const { data } = await api.post('/listings', listingData)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['listings'] })
        toast.success('Listing created successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to create listing')
      },
    }
  )
}

export const useUpdateListing = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    async ({ id, data: listingData }) => {
      const { data } = await api.put(`/listings/${id}`, listingData)
      return data
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['listing', variables.id] })
        queryClient.invalidateQueries({ queryKey: ['listings'] })
        toast.success('Listing updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to update listing')
      },
    }
  )
}

export const useDeleteListing = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    async (id) => {
      const { data } = await api.delete(`/listings/${id}`)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['listings'] })
        toast.success('Listing archived successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to archive listing')
      },
    }
  )
}

// Filter options
export const useFilterOptions = () => {
  return useQuery(
    'filterOptions',
    async () => {
      const { data } = await api.get('/listings/filters/options')
      return data
    },
    { staleTime: 5 * 60 * 1000 }
  )
}

// Suburbs for a single city — powers the "Select all suburbs" button in the
// prospect requirement editor. Lazy: only fires when `city` is truthy.
export const useSuburbsForCity = (city) => {
  return useQuery(
    ['suburbsForCity', city],
    async () => {
      const { data } = await api.get('/listings/filters/suburbs', { params: { city } })
      return data
    },
    { enabled: !!city, staleTime: 10 * 60 * 1000 }
  )
}

// Property type to uses mapping
// Cached for 7 days (604800000 ms) as this data changes infrequently
export const usePropertyTypeUses = () => {
  return useQuery(
    ['propertyTypeUses', 'v3'],  // HARD REFRESH: Fixed floor area values in property_use
    async () => {
      const { data } = await api.get('/discovery/property-type-uses')
      return data
    },
    { staleTime: 5 * 60 * 1000 }  // Reduced to 5 minutes for testing
  )
}

// Dashboard stats
export const useDashboardStats = () => {
  return useQuery(
    'dashboardStats',
    async () => {
      const { data } = await api.get('/dashboard/stats')
      return data
    },
    { refetchInterval: 30000 }
  )
}

// Contacts
export const useContacts = (params = {}) => {
  return useQuery(
    ['contacts', params],
    async () => {
      const { data } = await api.get('/contacts', { params })
      return data
    }
  )
}

// Mortgagee Sales API
export const useMortgageeSales = (params = {}, options = {}) => {
  return useQuery(
    ['mortgageeSales', params],
    async () => {
      const { data } = await api.get('/mortgagee-sales', { params })
      return data
    },
    { keepPreviousData: true, ...options }
  )
}

export const useMortgageeSaleDetail = (id) => {
  return useQuery(
    ['mortgageeSale', id],
    async () => {
      const { data } = await api.get(`/mortgagee-sales/${id}`)
      return data
    },
    { enabled: !!id }
  )
}

export const useCreateMortgageeSale = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (saleData) => {
      const { data } = await api.post('/mortgagee-sales', saleData)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['mortgageeSales'] })
      },
    }
  )
}

export const useExtractMortgageeSale = () => {
  return useMutation(
    async (id) => {
      const { data } = await api.post(`/mortgagee-sales/${id}/extract`)
      return data
    }
  )
}

export const useDeleteMortgageeSale = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (id) => {
      const { data } = await api.delete(`/mortgagee-sales/${id}`)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['mortgageeSales'] })
      },
    }
  )
}

export const useUpdateMortgageeSale = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, data }) => {
      const { data: response } = await api.put(`/mortgagee-sales/${id}`, data)
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['mortgageeSales'] })
      },
    }
  )
}

export const useMortgageeEmailPreview = () => {
  return useMutation(
    async ({ ids }) => {
      const { data } = await api.post('/mortgagee-sales/email-preview', { ids })
      return data
    }
  )
}

export const useMortgageeSendEmail = () => {
  return useMutation(
    async ({ recipients, subject, html_body, record_ids }) => {
      const { data } = await api.post('/mortgagee-sales/send-email', {
        recipients,
        subject,
        html_body,
        record_ids,
      })
      return data
    }
  )
}

export const useSyncMortgageeFromCommercialLease = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async () => {
      const { data } = await api.post('/mortgagee-sales/sync-from-commercial-lease')
      return data
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['mortgageeSales'] })
        toast.success(`Synced ${data.created} new, ${data.updated} updated, ${data.skipped} unchanged from Commercial Lease`)
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to sync from Commercial Lease')
      },
    }
  )
}

export const useCreateContact = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    async (contactData) => {
      const { data } = await api.post('/contacts', contactData)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['contacts'] })
        toast.success('Contact added successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to add contact')
      },
    }
  )
}

// Comments
export const useListingComments = (listingId) => {
  return useQuery(
    ['comments', listingId],
    async () => {
      const { data } = await api.get(`/listings/${listingId}/comments`)
      return data
    },
    { enabled: !!listingId }
  )
}

export const useAddComment = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    async ({ listingId, text }) => {
      const { data } = await api.post(`/listings/${listingId}/comments`, { text })
      return data
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['comments', variables.listingId] })
        toast.success('Comment added')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to add comment')
      },
    }
  )
}

// Search
export const useSearch = (query) => {
  return useQuery(
    ['search', query],
    async () => {
      const { data } = await api.get('/dashboard/quick-search', { params: { query } })
      return data
    },
    { enabled: !!query && query.length > 2 }
  )
}

// Commercial Lease API
export const useCommercialLeases = (params = {}) => {
  return useQuery(
    ['commercial-leases', params],
    async () => {
      const { data } = await api.get('/commercial-lease', { params })
      return data
    },
    { keepPreviousData: true }
  )
}

export const useCommercialLease = (id) => {
  return useQuery(
    ['commercial-lease', id],
    async () => {
      const { data } = await api.get(`/commercial-lease/${id}`)
      return data
    },
    { enabled: !!id }
  )
}

export const useCommercialLeaseFilterOptions = () => {
  return useQuery(
    'commercialLeaseFilterOptions',
    async () => {
      const { data } = await api.get('/commercial-lease/filters/options')
      return data
    },
    { staleTime: 5 * 60 * 1000 }
  )
}

export const useCommercialLeaseStats = () => {
  return useQuery(
    'commercialLeaseStats',
    async () => {
      const { data } = await api.get('/commercial-lease/stats')
      return data
    },
    { refetchInterval: 60000 }
  )
}

// Industrial Listings API
export const useIndustrialListings = (params = {}) => {
  return useQuery(
    ['industrial-listings', params],
    async () => {
      const { data } = await api.get('/industrial/listings', { params })
      return data
    },
    { keepPreviousData: true }
  )
}

export const useIndustrialListing = (id, includeCorelogic = true) => {
  return useQuery(
    ['industrial-listing', id, includeCorelogic],
    async () => {
      const { data } = await api.get(`/industrial/listings/${id}`, {
        params: includeCorelogic ? { include_corelogic: true } : {}
      })
      return data
    },
    { enabled: !!id }
  )
}

export const useIndustrialFilterOptions = () => {
  return useQuery(
    'industrialFilterOptions',
    async () => {
      const { data } = await api.get('/industrial/filters/options')
      return data
    },
    { staleTime: 5 * 60 * 1000 }
  )
}

export const useIndustrialStats = () => {
  return useQuery(
    'industrialStats',
    async () => {
      const { data } = await api.get('/industrial/stats')
      return data
    },
    { refetchInterval: 60000 }
  )
}

// Get all listing IDs matching current filters (for bulk selection)
export const useIndustrialListingIds = (params = {}, enabled = false) => {
  return useQuery(
    ['industrial-listing-ids', params],
    async () => {
      const { data } = await api.get('/industrial/listings/ids', { params })
      return data
    },
    {
      enabled: enabled, // Only fetch when explicitly enabled
      staleTime: 5 * 60 * 1000 // Cache for 5 minutes
    }
  )
}

// Hidden cards (server-backed, persisted per user). The previous implementation
// used localStorage which was device-local and never reached the backend; this
// hook talks to the auth-protected endpoints so hide state survives across
// devices and the listings/total counts can exclude the user's hidden rows.
export const useIndustrialHiddenCards = (userId) => {
  return useQuery(
    ['industrial-hidden-cards', userId],
    async () => {
      const { data } = await api.get(`/industrial/users/${userId}/hidden-cards`)
      return data  // { user_id, hidden_count, hidden_ids: [...] }
    },
    {
      enabled: !!userId,
      staleTime: 60 * 1000,
    }
  )
}

export const useHideIndustrialCard = (userId) => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ listingId, reason }) => {
      const { data } = await api.post(
        `/industrial/listings/${listingId}/hide`,
        null,
        { params: { user_id: userId, ...(reason ? { reason } : {}) } }
      )
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['industrial-hidden-cards', userId])
        queryClient.invalidateQueries(['industrial-listings'])
        queryClient.invalidateQueries(['industrial-listing-ids'])
      },
    }
  )
}

export const useUnhideIndustrialCard = (userId) => {
  const queryClient = useQueryClient()
  return useMutation(
    async (listingId) => {
      const { data } = await api.post(
        `/industrial/listings/${listingId}/unhide`,
        null,
        { params: { user_id: userId } }
      )
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['industrial-hidden-cards', userId])
        queryClient.invalidateQueries(['industrial-listings'])
        queryClient.invalidateQueries(['industrial-listing-ids'])
      },
    }
  )
}

// ============================================================================
// Prospect Matching System API
// ============================================================================

export const useProspectRequirements = (params = {}, options = {}) => {
  return useQuery(
    ['prospect-requirements', params],
    async () => {
      const { data } = await api.get('/prospects/requirements', { params })
      return data
    },
    { keepPreviousData: true, ...options }
  )
}

export const useProspectRequirementsSafe = (params = {}, options = {}) => {
  const result = useProspectRequirements(params, options)
  return {
    ...result,
    // Expose friendly error state for UI
    errorMessage: result.isError
      ? result.error?.response?.data?.detail || result.error?.message || 'Failed to load prospect requirements'
      : null,
    hasData: !!result.data,
  }
}

export const useProspectContacts = (params = {}, options = {}) => {
  return useQuery(
    ['prospect-contacts', params],
    async () => {
      const { data } = await api.get('/prospects/contacts', { params })
      return data
    },
    { keepPreviousData: true, ...options }
  )
}

export const useCreateProspectContact = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (contactData) => {
      const { data } = await api.post('/prospects/contacts', contactData)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prospect-contacts')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to create contact')
      },
    }
  )
}

export const useProspectRequirement = (id, options = {}) => {
  return useQuery(
    ['prospect-requirement', id],
    async () => {
      const { data } = await api.get(`/prospects/requirements/${id}`)
      return data
    },
    { enabled: !!id, ...options }
  )
}

export const useProspectRequirementSafe = (id, options = {}) => {
  const result = useProspectRequirement(id, options)
  return {
    ...result,
    errorMessage: result.isError
      ? result.error?.response?.data?.detail || result.error?.message || 'Failed to load prospect details'
      : null,
    hasData: !!result.data,
  }
}

export const useCreateProspectRequirement = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (reqData) => {
      const { data } = await api.post('/prospects/requirements', reqData)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prospect-requirements')
        toast.success('Prospect requirement created')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to create prospect requirement')
      },
    }
  )
}

export const useUpdateProspectRequirement = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, data: reqData }) => {
      const { data } = await api.put(`/prospects/requirements/${id}`, reqData)
      return data
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['prospect-requirement', variables.id] })
        queryClient.invalidateQueries('prospect-requirements')
        toast.success('Prospect requirement updated')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to update prospect requirement')
      },
    }
  )
}

export const useDeleteProspectRequirement = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (id) => {
      const { data } = await api.delete(`/prospects/requirements/${id}`)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prospect-requirements')
        toast.success('Prospect requirement archived')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to archive prospect requirement')
      },
    }
  )
}

export const useRunProspectMatch = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, min_score = 30, limit = 200 }) => {
      const { data } = await api.post(`/prospects/requirements/${id}/match`, null, {
        params: { min_score, limit }
      })
      return data
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['prospect-matches', variables.id] })
        queryClient.invalidateQueries('prospect-requirements')
        toast.success('Matching complete')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to run matching')
      },
    }
  )
}

export const useProspectMatches = (requirementId, params = {}, options = {}) => {
  return useQuery(
    ['prospect-matches', requirementId, params],
    async () => {
      const { data } = await api.get(`/prospects/requirements/${requirementId}/matches`, { params })
      return data
    },
    { enabled: !!requirementId, keepPreviousData: true, ...options }
  )
}

export const useProspectMatchesSafe = (requirementId, params = {}) => {
  const result = useProspectMatches(requirementId, params)
  return {
    ...result,
    errorMessage: result.isError
      ? result.error?.response?.data?.detail || result.error?.message || 'Failed to load matches'
      : null,
    hasData: !!result.data,
  }
}

export const useAllProspectMatches = (params = {}) => {
  return useQuery(
    ['prospect-matches-all', params],
    async () => {
      const { data } = await api.get('/prospects/matches', { params })
      return data
    },
    { keepPreviousData: true }
  )
}

export const useUpdateMatchStatus = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, status, notes }) => {
      const params = { status }
      if (notes) params.notes = notes
      const { data } = await api.put(`/prospects/matches/${id}/status`, null, { params })
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prospect-matches')
        queryClient.invalidateQueries('prospect-matches-all')
        queryClient.invalidateQueries('prospect-top-matches')
        queryClient.invalidateQueries('prospect-requirement')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to update match status')
      },
    }
  )
}

export const useContactActivities = (contactId, params = {}) => {
  return useQuery(
    ['contact-activities', contactId, params],
    async () => {
      const { data } = await api.get('/prospects/activities', {
        params: { contact_id: contactId, ...params }
      })
      return data
    },
    { enabled: !!contactId }
  )
}

export const useProspectStats = () => {
  return useQuery(
    'prospectStats',
    async () => {
      const { data } = await api.get('/prospects/stats')
      return data
    },
    { refetchInterval: 30000 }
  )
}

export const useProspectStatsSafe = () => {
  const result = useProspectStats()
  return {
    ...result,
    errorMessage: result.isError
      ? result.error?.response?.data?.detail || result.error?.message || 'Failed to load prospect statistics'
      : null,
    hasData: !!result.data,
  }
}

export const useProspectEnquirySuggestions = (params = {}, options = {}) => {
  return useQuery(
    ['prospect-enquiry-suggestions', params],
    async () => {
      const { data } = await api.get('/prospects/enquiry-suggestions', { params })
      return data
    },
    { enabled: !!params.contact_id || !!params.email || !!params.phone, ...options }
  )
}

export const useCreateContactActivity = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (activityData) => {
      const { data } = await api.post('/prospects/activities', activityData)
      return data
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['contact-activities', variables.contact_id] })
        toast.success('Activity added')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to add activity')
      },
    }
  )
}

export const useQuickEditRequirement = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, field, value }) => {
      const { data } = await api.put(`/prospects/requirements/${id}/quick-edit`, { field, value })
      return data
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['prospect-requirement', variables.id] })
        queryClient.invalidateQueries('prospect-requirements')
      },
    }
  )
}

export const useBulkUpdateMatchStatus = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ matchIds, status }) => {
      const { data } = await api.put('/prospects/matches/bulk-status', { match_ids: matchIds, status })
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prospect-matches')
        queryClient.invalidateQueries('prospect-matches-all')
        queryClient.invalidateQueries('prospect-top-matches')
        queryClient.invalidateQueries('prospect-requirement')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to update match statuses')
      },
    }
  )
}

export const useTopMatches = (requirementId, limit = 12, options = {}) => {
  return useQuery(
    ['prospect-top-matches', requirementId, limit],
    async () => {
      const { data } = await api.get(`/prospects/requirements/${requirementId}/top-matches`, { params: { limit } })
      return data
    },
    { enabled: !!requirementId, ...options }
  )
}

export const usePreviewProspectEmail = () => {
  return useMutation(
    async ({ requirementId, matchIds, subject, message }) => {
      const { data } = await api.post(`/prospects/requirements/${requirementId}/email-preview`, {
        match_ids: matchIds,
        subject,
        message,
      })
      return data
    },
    {
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to generate preview')
      },
    }
  )
}

export const useSendProspectEmail = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ requirementId, matchIds, subject, message }) => {
      const { data } = await api.post(`/prospects/requirements/${requirementId}/send-email`, {
        match_ids: matchIds,
        subject,
        message,
      })
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contact-activities')
        toast.success('Email sent successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to send email')
      },
    }
  )
}

// ============================================================================
// Public Prospect Page API (no auth required)
// ============================================================================

export const usePublicProspect = (token) => {
  return useQuery(
    ['public-prospect', token],
    async () => {
      const { data } = await api.get(`/prospects/public/${token}`)
      return data
    },
    { enabled: !!token, retry: 1 }
  )
}

export const usePublicLikeMatch = () => {
  return useMutation(
    async ({ token, matchId }) => {
      const { data } = await api.post(`/prospects/public/${token}/like/${matchId}`)
      return data
    }
  )
}

export const usePublicDislikeMatch = () => {
  return useMutation(
    async ({ token, matchId }) => {
      const { data } = await api.post(`/prospects/public/${token}/dislike/${matchId}`)
      return data
    }
  )
}

// Unified Contacts (consolidated ultimatecontacts master on jlsys_contacts)
export const useUnifiedContacts = (params = {}, options = {}) => {
  return useQuery(
    ['unifiedContacts', params],
    async () => {
      const { data } = await api.get('/unified-contacts', { params })
      return data
    },
    { keepPreviousData: true, ...options }
  )
}

export const useUnifiedContactsStats = () => {
  return useQuery(
    'unifiedContactsStats',
    async () => {
      const { data } = await api.get('/unified-contacts/stats')
      return data
    },
    { refetchInterval: 60000 }
  )
}

export const useUnifiedContactsSources = () => {
  return useQuery(
    'unifiedContactsSources',
    async () => {
      const { data } = await api.get('/unified-contacts/sources')
      return data
    },
    { staleTime: 10 * 60 * 1000 }
  )
}

export const useUnifiedContactDetail = (id, options = {}) => {
  return useQuery(
    ['unifiedContact', id],
    async () => {
      const { data } = await api.get(`/unified-contacts/${id}`)
      return data
    },
    { enabled: !!id, ...options }
  )
}

export const useUpdateUnifiedContact = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, data: contactData }) => {
      const { data } = await api.put(`/unified-contacts/${id}`, contactData)
      return data
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['unifiedContact', variables.id] })
        queryClient.invalidateQueries({ queryKey: ['unifiedContacts'] })
        toast.success('Contact updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to update contact')
      },
    }
  )
}

export const useDeleteUnifiedContact = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (id) => {
      const { data } = await api.delete(`/unified-contacts/${id}`)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['unifiedContacts'] })
        queryClient.invalidateQueries({ queryKey: ['unifiedContactsStats'] })
        toast.success('Contact removed')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to remove contact')
      },
    }
  )
}

export const useMergeUnifiedContacts = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ masterId, duplicateIds }) => {
      const { data } = await api.post(`/unified-contacts/${masterId}/merge`, {
        duplicate_ids: duplicateIds,
      })
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['unifiedContacts'] })
        queryClient.invalidateQueries({ queryKey: ['unifiedContactsStats'] })
        queryClient.invalidateQueries({ queryKey: ['unifiedContact'] })
        toast.success('Duplicates merged into master')
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to merge duplicates')
      },
    }
  )
}

export default api
